import { eligibleEnemies } from './eligibility.js';
import { fingerprintDungeonPlan } from './fingerprint.js';
import { resolveEnemyFootprint, validateClearanceProfile, validateFootprint, validatePacingProfile, validateThemeLayout, } from './layout.js';
import { DUNGEON_CONNECTION_KINDS, DUNGEON_OBJECTIVE_KINDS, DUNGEON_ROOM_ROLES, } from './types.js';
const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function problem(problems, code, path, message) {
    problems.push({ code, path, message });
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function checkId(value, path, problems) {
    if (typeof value !== 'string' || !KEBAB_CASE.test(value)) {
        problem(problems, 'invalid-id', path, 'must be a non-empty kebab-case string');
        return false;
    }
    return true;
}
function checkProbability(value, path, problems) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) {
        problem(problems, 'invalid-probability', path, 'must be a finite number in [0, 1]');
    }
}
function checkPositiveNumber(value, path, problems) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        problem(problems, 'invalid-positive-number', path, 'must be a finite number greater than 0');
    }
}
function checkPositiveInteger(value, path, problems) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
        problem(problems, 'invalid-positive-integer', path, 'must be a positive integer');
    }
}
function duplicateIds(values, path, problems) {
    const seen = new Set();
    for (const value of values) {
        if (!isRecord(value) || typeof value.id !== 'string')
            continue;
        if (seen.has(value.id))
            problem(problems, 'duplicate-id', path, `duplicate id '${value.id}'`);
        seen.add(value.id);
    }
}
export function validateDungeonCatalog(catalog) {
    const problems = [];
    if (!isRecord(catalog)) {
        problem(problems, 'invalid-catalog', 'catalog', 'must be an object');
        return problems;
    }
    if (typeof catalog['version'] !== 'string' || catalog['version'].length === 0) {
        problem(problems, 'invalid-version', 'catalog.version', 'must be a non-empty string');
    }
    for (const field of ['difficulties', 'themes', 'mutators', 'enemies']) {
        if (!Array.isArray(catalog[field])) {
            problem(problems, 'invalid-array', `catalog.${field}`, 'must be an array');
        }
    }
    if (problems.some((entry) => entry.code === 'invalid-array'))
        return problems;
    const typed = catalog;
    duplicateIds(typed.difficulties, 'catalog.difficulties', problems);
    duplicateIds(typed.themes, 'catalog.themes', problems);
    duplicateIds(typed.mutators, 'catalog.mutators', problems);
    duplicateIds(typed.enemies, 'catalog.enemies', problems);
    const difficultyIds = new Set();
    for (const [index, profile] of typed.difficulties.entries()) {
        const path = `catalog.difficulties[${index}]`;
        if (!isRecord(profile)) {
            problem(problems, 'invalid-profile', path, 'must be an object');
            continue;
        }
        if (checkId(profile.id, `${path}.id`, problems))
            difficultyIds.add(profile.id);
        if (typeof profile.name !== 'string' || profile.name.length === 0) {
            problem(problems, 'invalid-name', `${path}.name`, 'must be a non-empty string');
        }
        checkPositiveInteger(profile.rank, `${path}.rank`, problems);
        if (!isRecord(profile.roomCount)) {
            problem(problems, 'invalid-range', `${path}.roomCount`, 'must be { min, max }');
        }
        else {
            checkPositiveInteger(profile.roomCount.min, `${path}.roomCount.min`, problems);
            checkPositiveInteger(profile.roomCount.max, `${path}.roomCount.max`, problems);
            if (typeof profile.roomCount.min === 'number' &&
                typeof profile.roomCount.max === 'number' &&
                profile.roomCount.max < profile.roomCount.min) {
                problem(problems, 'invalid-range', `${path}.roomCount`, 'max must be >= min');
            }
            if (typeof profile.roomCount.min === 'number' && profile.roomCount.min < 5) {
                problem(problems, 'invalid-range', `${path}.roomCount.min`, 'must allow at least 5 rooms');
            }
        }
        checkPositiveInteger(profile.bossCount, `${path}.bossCount`, problems);
        if (typeof profile.bossCount === 'number' && profile.bossCount > 2) {
            problem(problems, 'invalid-boss-count', `${path}.bossCount`, 'must be 1 or 2');
        }
        checkProbability(profile.loopChance, `${path}.loopChance`, problems);
        checkProbability(profile.combatRoomChance, `${path}.combatRoomChance`, problems);
        checkProbability(profile.puzzleRoomChance, `${path}.puzzleRoomChance`, problems);
        checkProbability(profile.treasureRoomChance, `${path}.treasureRoomChance`, problems);
        checkPositiveInteger(profile.restEveryRooms, `${path}.restEveryRooms`, problems);
        checkPositiveNumber(profile.baseThreatPerPlayer, `${path}.baseThreatPerPlayer`, problems);
        checkProbability(profile.depthThreatBonus, `${path}.depthThreatBonus`, problems);
        checkPositiveInteger(profile.maxEncounterSize, `${path}.maxEncounterSize`, problems);
        if (!Number.isInteger(profile.mutatorCount) || profile.mutatorCount < 0) {
            problem(problems, 'invalid-mutator-count', `${path}.mutatorCount`, 'must be an integer >= 0');
        }
        checkPositiveNumber(profile.bossDifficultyScale, `${path}.bossDifficultyScale`, problems);
        if (typeof profile.rewardClass !== 'string' || profile.rewardClass.length === 0) {
            problem(problems, 'invalid-reward-class', `${path}.rewardClass`, 'must be non-empty');
        }
        // Required from S2 (AD-404): the layout stage consumes both.
        problems.push(...validateClearanceProfile(profile.clearance, `${path}.clearance`));
        problems.push(...validatePacingProfile(profile.pacing, `${path}.pacing`));
    }
    const mutatorIds = new Set();
    for (const [index, mutator] of typed.mutators.entries()) {
        const path = `catalog.mutators[${index}]`;
        if (!isRecord(mutator)) {
            problem(problems, 'invalid-mutator', path, 'must be an object');
            continue;
        }
        if (checkId(mutator.id, `${path}.id`, problems))
            mutatorIds.add(mutator.id);
        if (!Array.isArray(mutator.allowedDifficultyIds)) {
            problem(problems, 'invalid-array', `${path}.allowedDifficultyIds`, 'must be an array');
        }
        else {
            for (const difficultyId of mutator.allowedDifficultyIds) {
                if (typeof difficultyId !== 'string' || !difficultyIds.has(difficultyId)) {
                    problem(problems, 'unknown-difficulty', `${path}.allowedDifficultyIds`, `unknown difficulty '${String(difficultyId)}'`);
                }
            }
        }
        if (!Array.isArray(mutator.compatibleThemeTags) || mutator.compatibleThemeTags.length === 0) {
            problem(problems, 'invalid-tags', `${path}.compatibleThemeTags`, 'must be a non-empty array');
        }
        checkPositiveNumber(mutator.selectionWeight, `${path}.selectionWeight`, problems);
        if (!isRecord(mutator.effects)) {
            problem(problems, 'invalid-effects', `${path}.effects`, 'must be an object');
        }
        else {
            const multiplier = mutator.effects.encounterBudgetMultiplier;
            if (multiplier !== undefined) {
                checkPositiveNumber(multiplier, `${path}.effects.encounterBudgetMultiplier`, problems);
            }
            const bonus = mutator.effects.loopChanceBonus;
            if (bonus !== undefined)
                checkProbability(bonus, `${path}.effects.loopChanceBonus`, problems);
            const optionalBonus = mutator.effects.optionalRoomChanceBonus;
            if (optionalBonus !== undefined) {
                checkProbability(optionalBonus, `${path}.effects.optionalRoomChanceBonus`, problems);
            }
            const sizeBonus = mutator.effects.maxEncounterSizeBonus;
            if (sizeBonus !== undefined &&
                (typeof sizeBonus !== 'number' || !Number.isInteger(sizeBonus) || sizeBonus < 0)) {
                problem(problems, 'invalid-size-bonus', `${path}.effects.maxEncounterSizeBonus`, 'must be an integer >= 0');
            }
            const preferredTags = mutator.effects.preferEnemyTags;
            if (preferredTags !== undefined &&
                (!Array.isArray(preferredTags) ||
                    preferredTags.length === 0 ||
                    preferredTags.some((tag) => typeof tag !== 'string' || tag.length === 0))) {
                problem(problems, 'invalid-preferred-tags', `${path}.effects.preferEnemyTags`, 'must be a non-empty array of strings when present');
            }
        }
    }
    for (const [index, theme] of typed.themes.entries()) {
        const path = `catalog.themes[${index}]`;
        if (!isRecord(theme)) {
            problem(problems, 'invalid-theme', path, 'must be an object');
            continue;
        }
        checkId(theme.id, `${path}.id`, problems);
        if (!isRecord(theme.tierRange)) {
            problem(problems, 'invalid-range', `${path}.tierRange`, 'must be { min, max }');
        }
        else {
            checkPositiveInteger(theme.tierRange.min, `${path}.tierRange.min`, problems);
            checkPositiveInteger(theme.tierRange.max, `${path}.tierRange.max`, problems);
            if (typeof theme.tierRange.min === 'number' &&
                typeof theme.tierRange.max === 'number' &&
                theme.tierRange.max < theme.tierRange.min) {
                problem(problems, 'invalid-range', `${path}.tierRange`, 'max must be >= min');
            }
        }
        if (!Array.isArray(theme.enemyTags) || theme.enemyTags.length === 0) {
            problem(problems, 'invalid-tags', `${path}.enemyTags`, 'must be a non-empty array');
        }
        if (!Array.isArray(theme.mutatorIds)) {
            problem(problems, 'invalid-array', `${path}.mutatorIds`, 'must be an array');
        }
        else {
            for (const mutatorId of theme.mutatorIds) {
                if (typeof mutatorId !== 'string' || !mutatorIds.has(mutatorId)) {
                    problem(problems, 'unknown-mutator', `${path}.mutatorIds`, `unknown mutator '${String(mutatorId)}'`);
                }
            }
        }
        // Retained for v1 plans and ignored by the generator since S2 (AD-404 §4.3).
        checkPositiveNumber(theme.roomSpacing, `${path}.roomSpacing`, problems);
        problems.push(...validateThemeLayout(theme.layout, `${path}.layout`));
        if (!Array.isArray(theme.roomTemplates) || theme.roomTemplates.length === 0) {
            problem(problems, 'invalid-templates', `${path}.roomTemplates`, 'must be a non-empty array');
            continue;
        }
        duplicateIds(theme.roomTemplates, `${path}.roomTemplates`, problems);
        const coveredRoles = new Set();
        for (const [templateIndex, template] of theme.roomTemplates.entries()) {
            const templatePath = `${path}.roomTemplates[${templateIndex}]`;
            checkId(template.id, `${templatePath}.id`, problems);
            if (!Array.isArray(template.roles) || template.roles.length === 0) {
                problem(problems, 'invalid-roles', `${templatePath}.roles`, 'must be a non-empty array');
            }
            else {
                for (const role of template.roles) {
                    if (!DUNGEON_ROOM_ROLES.includes(role)) {
                        problem(problems, 'unknown-role', `${templatePath}.roles`, `unknown role '${String(role)}'`);
                    }
                    else {
                        coveredRoles.add(role);
                    }
                }
            }
            if (typeof template.assetPackId !== 'string' || template.assetPackId.length === 0) {
                problem(problems, 'invalid-asset-pack', `${templatePath}.assetPackId`, 'must be non-empty');
            }
            if (!Array.isArray(template.assetIds) || template.assetIds.length === 0) {
                problem(problems, 'invalid-assets', `${templatePath}.assetIds`, 'must be non-empty');
            }
            checkPositiveNumber(template.selectionWeight, `${templatePath}.selectionWeight`, problems);
        }
        for (const requiredRole of ['entrance', 'combat', 'miniboss', 'boss', 'exit']) {
            if (!coveredRoles.has(requiredRole)) {
                problem(problems, 'missing-template-role', `${path}.roomTemplates`, `must cover the '${requiredRole}' role`);
            }
        }
    }
    for (const [index, enemy] of typed.enemies.entries()) {
        const path = `catalog.enemies[${index}]`;
        if (!isRecord(enemy)) {
            problem(problems, 'invalid-enemy', path, 'must be an object');
            continue;
        }
        checkId(enemy.id, `${path}.id`, problems);
        checkPositiveInteger(enemy.tier, `${path}.tier`, problems);
        checkPositiveNumber(enemy.threat, `${path}.threat`, problems);
        checkPositiveNumber(enemy.selectionWeight, `${path}.selectionWeight`, problems);
        if (!Array.isArray(enemy.roles) || enemy.roles.length === 0) {
            problem(problems, 'invalid-roles', `${path}.roles`, 'must be a non-empty array');
        }
        if (!Array.isArray(enemy.tags) || enemy.tags.length === 0) {
            problem(problems, 'invalid-tags', `${path}.tags`, 'must be a non-empty array');
        }
        if (enemy.boss !== undefined && typeof enemy.boss !== 'boolean') {
            problem(problems, 'invalid-boss-flag', `${path}.boss`, 'must be boolean when present');
        }
        if (enemy.footprint !== undefined) {
            problems.push(...validateFootprint(enemy.footprint, `${path}.footprint`, true));
        }
    }
    const defaults = catalog['footprintDefaults'];
    if (!isRecord(defaults)) {
        problem(problems, 'invalid-footprint', 'catalog.footprintDefaults', 'must be an object');
    }
    else {
        let defaultsValid = true;
        for (const [role, footprint] of Object.entries(defaults)) {
            const found = validateFootprint(footprint, `catalog.footprintDefaults.${role}`, false);
            if (found.length > 0)
                defaultsValid = false;
            problems.push(...found);
        }
        // The layout stage sizes chambers from these; an enemy it cannot size would fail every attempt.
        if (defaultsValid) {
            for (const [index, enemy] of typed.enemies.entries()) {
                if (!isRecord(enemy) || !Array.isArray(enemy.roles))
                    continue;
                const rules = {
                    enemies: typed.enemies,
                    footprintDefaults: defaults,
                };
                if (!resolveEnemyFootprint(String(enemy.id), [], rules)) {
                    problem(problems, 'missing-footprint', `catalog.enemies[${index}]`, 'no authored footprint and no footprintDefaults entry for any of its roles');
                }
            }
        }
    }
    return problems;
}
export function validateGenerateDungeonRequest(request, catalog) {
    const problems = [];
    if (!isRecord(request)) {
        problem(problems, 'invalid-request', 'request', 'must be an object');
        return problems;
    }
    const seed = request['seed'];
    if (!((typeof seed === 'string' && seed.length > 0) ||
        (typeof seed === 'number' && Number.isFinite(seed)))) {
        problem(problems, 'invalid-seed', 'request.seed', 'must be a non-empty string or finite number');
    }
    const profile = catalog.difficulties.find((entry) => entry.id === request['difficultyId']);
    if (!profile) {
        problem(problems, 'unknown-difficulty', 'request.difficultyId', `unknown difficulty '${String(request['difficultyId'])}'`);
    }
    const theme = catalog.themes.find((entry) => entry.id === request['themeId']);
    if (!theme) {
        problem(problems, 'unknown-theme', 'request.themeId', `unknown theme '${String(request['themeId'])}'`);
    }
    checkPositiveInteger(request['targetTier'], 'request.targetTier', problems);
    checkPositiveInteger(request['partySize'], 'request.partySize', problems);
    if (typeof request['partySize'] === 'number' && request['partySize'] > 8) {
        problem(problems, 'party-too-large', 'request.partySize', 'must be <= 8 for this feature version');
    }
    if (request['maxAttempts'] !== undefined) {
        checkPositiveInteger(request['maxAttempts'], 'request.maxAttempts', problems);
        if (typeof request['maxAttempts'] === 'number' && request['maxAttempts'] > 32) {
            problem(problems, 'too-many-attempts', 'request.maxAttempts', 'must be <= 32');
        }
    }
    if (profile && request['roomCount'] !== undefined) {
        const roomCount = request['roomCount'];
        if (typeof roomCount !== 'number' ||
            !Number.isInteger(roomCount) ||
            roomCount < profile.roomCount.min ||
            roomCount > profile.roomCount.max) {
            problem(problems, 'room-count-out-of-range', 'request.roomCount', `must be an integer in [${profile.roomCount.min}, ${profile.roomCount.max}]`);
        }
    }
    if (profile && request['bossCount'] !== undefined) {
        const bossCount = request['bossCount'];
        if (typeof bossCount !== 'number' ||
            !Number.isInteger(bossCount) ||
            bossCount < 1 ||
            bossCount > profile.bossCount) {
            problem(problems, 'boss-count-out-of-range', 'request.bossCount', `must be an integer in [1, ${profile.bossCount}]`);
        }
    }
    if (theme && typeof request['targetTier'] === 'number') {
        const targetTier = request['targetTier'];
        if (targetTier < theme.tierRange.min || targetTier > theme.tierRange.max) {
            problem(problems, 'tier-out-of-theme-range', 'request.targetTier', `must be in theme range [${theme.tierRange.min}, ${theme.tierRange.max}]`);
        }
        const ordinary = eligibleEnemies(catalog.enemies, theme, targetTier, false);
        if (ordinary.length === 0) {
            problem(problems, 'no-eligible-enemies', 'catalog.enemies', `no ordinary enemies match theme '${theme.id}' and tier ${targetTier}`);
        }
        const requestedBosses = typeof request['bossCount'] === 'number' && Number.isInteger(request['bossCount'])
            ? request['bossCount']
            : (profile?.bossCount ?? 1);
        const bosses = eligibleEnemies(catalog.enemies, theme, targetTier, true);
        if (bosses.length < requestedBosses) {
            problem(problems, 'insufficient-eligible-bosses', 'catalog.enemies', `needs ${requestedBosses} unique boss(es), found ${bosses.length}`);
        }
        if (profile) {
            const availableMutators = catalog.mutators.filter((mutator) => theme.mutatorIds.includes(mutator.id) &&
                mutator.allowedDifficultyIds.includes(profile.id) &&
                mutator.compatibleThemeTags.some((tag) => theme.enemyTags.includes(tag)));
            if (availableMutators.length < profile.mutatorCount) {
                problem(problems, 'insufficient-mutators', 'catalog.mutators', `difficulty '${profile.id}' needs ${profile.mutatorCount}, found ${availableMutators.length}`);
            }
        }
    }
    return problems;
}
function roomById(plan, roomId) {
    return plan.rooms.find((room) => room.id === roomId);
}
function connectionKey(a, b) {
    return a < b ? `${a}|${b}` : `${b}|${a}`;
}
export function validateDungeonPlan(plan, catalog) {
    const problems = [];
    if (!isRecord(plan)) {
        problem(problems, 'invalid-plan', 'plan', 'must be an object');
        return problems;
    }
    if (!Array.isArray(plan['rooms']) || !Array.isArray(plan['connections'])) {
        problem(problems, 'invalid-plan-shape', 'plan', 'rooms and connections must be arrays');
        return problems;
    }
    if (!Array.isArray(plan['objectives']) || !Array.isArray(plan['rewardIntents'])) {
        problem(problems, 'invalid-plan-shape', 'plan', 'objectives and rewardIntents must be arrays');
        return problems;
    }
    for (const field of ['bossRoomIds', 'criticalPathRoomIds', 'mutatorIds']) {
        if (!Array.isArray(plan[field])) {
            problem(problems, 'invalid-plan-shape', `plan.${field}`, 'must be an array');
        }
    }
    if (problems.length > 0)
        return problems;
    for (const field of ['rooms', 'connections', 'objectives', 'rewardIntents']) {
        const entries = plan[field];
        if (entries.some((entry) => !isRecord(entry))) {
            problem(problems, 'invalid-plan-entry', `plan.${field}`, 'every entry must be an object');
        }
    }
    if (problems.length > 0)
        return problems;
    const typed = plan;
    const profile = catalog.difficulties.find((entry) => entry.id === typed.difficultyId);
    const theme = catalog.themes.find((entry) => entry.id === typed.themeId);
    if (!profile)
        problem(problems, 'unknown-difficulty', 'plan.difficultyId', 'not in catalogue');
    if (!theme)
        problem(problems, 'unknown-theme', 'plan.themeId', 'not in catalogue');
    if (profile &&
        (typed.rooms.length < profile.roomCount.min || typed.rooms.length > profile.roomCount.max)) {
        problem(problems, 'room-count-out-of-range', 'plan.rooms', `must contain ${profile.roomCount.min}-${profile.roomCount.max} rooms`);
    }
    if (profile && typed.mutatorIds.length !== profile.mutatorCount) {
        problem(problems, 'mutator-count-mismatch', 'plan.mutatorIds', `expected ${profile.mutatorCount}, found ${typed.mutatorIds.length}`);
    }
    for (const mutatorId of typed.mutatorIds) {
        if (!catalog.mutators.some((mutator) => mutator.id === mutatorId)) {
            problem(problems, 'unknown-mutator', 'plan.mutatorIds', mutatorId);
        }
    }
    duplicateIds(typed.rooms, 'plan.rooms', problems);
    duplicateIds(typed.connections, 'plan.connections', problems);
    duplicateIds(typed.objectives, 'plan.objectives', problems);
    duplicateIds(typed.rewardIntents, 'plan.rewardIntents', problems);
    const roomIds = new Set(typed.rooms.map((room) => room.id));
    const coordinateKeys = new Set();
    for (const [index, room] of typed.rooms.entries()) {
        const path = `plan.rooms[${index}]`;
        if (!DUNGEON_ROOM_ROLES.includes(room.role)) {
            problem(problems, 'unknown-role', `${path}.role`, `unknown role '${String(room.role)}'`);
        }
        const coordinateKey = `${room.coordinate?.x},${room.coordinate?.z}`;
        if (coordinateKeys.has(coordinateKey)) {
            problem(problems, 'duplicate-coordinate', `${path}.coordinate`, coordinateKey);
        }
        coordinateKeys.add(coordinateKey);
        if (theme && !theme.roomTemplates.some((template) => template.id === room.templateId)) {
            problem(problems, 'unknown-template', `${path}.templateId`, room.templateId);
        }
        if (room.encounter) {
            for (const member of room.encounter.members) {
                const enemy = catalog.enemies.find((entry) => entry.id === member.enemyId);
                if (!enemy) {
                    problem(problems, 'unknown-enemy', `${path}.encounter.members`, member.enemyId);
                }
                else if (room.encounter.kind === 'boss' && !enemy.boss) {
                    problem(problems, 'ordinary-as-boss', `${path}.encounter.members`, member.enemyId);
                }
                else if (room.encounter.kind === 'ordinary' && enemy.boss) {
                    problem(problems, 'boss-as-ordinary', `${path}.encounter.members`, member.enemyId);
                }
            }
        }
    }
    const edgeKeys = new Set();
    const adjacency = new Map();
    for (const roomId of roomIds)
        adjacency.set(roomId, new Set());
    for (const [index, edge] of typed.connections.entries()) {
        const path = `plan.connections[${index}]`;
        if (!roomIds.has(edge.a) || !roomIds.has(edge.b) || edge.a === edge.b) {
            problem(problems, 'invalid-edge-endpoints', path, `edge '${edge.a}' -> '${edge.b}' is invalid`);
            continue;
        }
        if (!DUNGEON_CONNECTION_KINDS.includes(edge.kind)) {
            problem(problems, 'unknown-connection-kind', `${path}.kind`, String(edge.kind));
        }
        const key = connectionKey(edge.a, edge.b);
        if (edgeKeys.has(key))
            problem(problems, 'duplicate-edge', path, key);
        edgeKeys.add(key);
        adjacency.get(edge.a)?.add(edge.b);
        adjacency.get(edge.b)?.add(edge.a);
    }
    if (!roomIds.has(typed.entryRoomId)) {
        problem(problems, 'unknown-entry', 'plan.entryRoomId', typed.entryRoomId);
    }
    if (!roomIds.has(typed.exitRoomId)) {
        problem(problems, 'unknown-exit', 'plan.exitRoomId', typed.exitRoomId);
    }
    if (roomById(typed, typed.entryRoomId)?.role !== 'entrance') {
        problem(problems, 'invalid-entry-role', 'plan.entryRoomId', 'entry room must have entrance role');
    }
    if (roomById(typed, typed.exitRoomId)?.role !== 'exit') {
        problem(problems, 'invalid-exit-role', 'plan.exitRoomId', 'exit room must have exit role');
    }
    const visited = new Set();
    const queue = roomIds.has(typed.entryRoomId) ? [typed.entryRoomId] : [];
    while (queue.length > 0) {
        const current = queue.shift();
        if (visited.has(current))
            continue;
        visited.add(current);
        for (const next of adjacency.get(current) ?? [])
            if (!visited.has(next))
                queue.push(next);
    }
    if (visited.size !== roomIds.size) {
        problem(problems, 'disconnected-layout', 'plan.connections', `${visited.size}/${roomIds.size} reachable`);
    }
    const path = typed.criticalPathRoomIds;
    if (!Array.isArray(path) ||
        path[0] !== typed.entryRoomId ||
        path[path.length - 1] !== typed.exitRoomId) {
        problem(problems, 'invalid-critical-path', 'plan.criticalPathRoomIds', 'must run from entry to exit');
    }
    else {
        for (let index = 1; index < path.length; index += 1) {
            if (!edgeKeys.has(connectionKey(path[index - 1], path[index]))) {
                problem(problems, 'broken-critical-path', `plan.criticalPathRoomIds[${index}]`, 'consecutive rooms are not connected');
            }
        }
        if (path[path.length - 2] !== typed.finalBossRoomId) {
            problem(problems, 'boss-not-before-exit', 'plan.finalBossRoomId', 'final boss must immediately precede the exit');
        }
    }
    if (!typed.bossRoomIds.includes(typed.finalBossRoomId)) {
        problem(problems, 'missing-final-boss', 'plan.bossRoomIds', 'must include finalBossRoomId');
    }
    if (profile && typed.bossRoomIds.length < 1) {
        problem(problems, 'missing-boss', 'plan.bossRoomIds', 'must contain at least one boss');
    }
    for (const bossRoomId of typed.bossRoomIds) {
        const room = roomById(typed, bossRoomId);
        if (!room || !['miniboss', 'boss'].includes(room.role) || room.encounter?.kind !== 'boss') {
            problem(problems, 'invalid-boss-room', 'plan.bossRoomIds', bossRoomId);
        }
    }
    const objectiveIds = new Set(typed.objectives.map((objective) => objective.id));
    for (const [index, objective] of typed.objectives.entries()) {
        const objectivePath = `plan.objectives[${index}]`;
        if (!DUNGEON_OBJECTIVE_KINDS.includes(objective.kind)) {
            problem(problems, 'unknown-objective-kind', `${objectivePath}.kind`, String(objective.kind));
        }
        if (!roomIds.has(objective.roomId)) {
            problem(problems, 'unknown-objective-room', `${objectivePath}.roomId`, objective.roomId);
        }
        for (const dependency of objective.dependsOn) {
            if (!objectiveIds.has(dependency)) {
                problem(problems, 'unknown-objective-dependency', `${objectivePath}.dependsOn`, dependency);
            }
        }
    }
    const finalBossObjective = typed.objectives.find((objective) => objective.roomId === typed.finalBossRoomId && objective.kind === 'defeat-boss');
    if (!finalBossObjective) {
        problem(problems, 'missing-final-boss-objective', 'plan.objectives', 'final boss requires a defeat-boss objective');
    }
    const completionObjective = typed.objectives.find((objective) => objective.roomId === typed.exitRoomId && objective.kind === 'complete-dungeon');
    if (!completionObjective ||
        !finalBossObjective ||
        !completionObjective.dependsOn.includes(finalBossObjective.id)) {
        problem(problems, 'invalid-completion-objective', 'plan.objectives', 'completion must depend on the final boss objective');
    }
    const finalGate = typed.connections.find((edge) => connectionKey(edge.a, edge.b) === connectionKey(typed.finalBossRoomId, typed.exitRoomId));
    if (!finalGate ||
        finalGate.kind !== 'sealed-gate' ||
        !finalBossObjective ||
        finalGate.lockedByObjectiveId !== finalBossObjective.id) {
        problem(problems, 'invalid-final-gate', 'plan.connections', 'boss-to-exit edge must be sealed by the final boss objective');
    }
    const forbiddenRewardKeys = new Set([
        'item',
        'itemId',
        'items',
        'shards',
        'gleam',
        'currency',
        'xp',
        'power',
        'rewards',
    ]);
    for (const [index, intent] of typed.rewardIntents.entries()) {
        const rewardPath = `plan.rewardIntents[${index}]`;
        if (intent.authority !== 'existing-reward-services') {
            problem(problems, 'invalid-reward-authority', `${rewardPath}.authority`, String(intent.authority));
        }
        if (!finalBossObjective || !intent.unlockObjectiveIds.includes(finalBossObjective.id)) {
            problem(problems, 'reward-before-final-boss', `${rewardPath}.unlockObjectiveIds`, 'must include the final boss objective');
        }
        for (const key of Object.keys(intent)) {
            if (forbiddenRewardKeys.has(key)) {
                problem(problems, 'concrete-reward-payload', rewardPath, `forbidden key '${key}'`);
            }
        }
    }
    if (typeof typed.fingerprint === 'string') {
        const expected = fingerprintDungeonPlan(typed);
        if (typed.fingerprint !== expected) {
            problem(problems, 'fingerprint-mismatch', 'plan.fingerprint', `expected '${expected}'`);
        }
        if (typed.id !== `dungeon-${expected.slice(0, 12)}`) {
            problem(problems, 'plan-id-mismatch', 'plan.id', 'must derive from the plan fingerprint');
        }
    }
    return problems;
}
export function profileById(catalog, id) {
    return catalog.difficulties.find((profile) => profile.id === id);
}
export function themeById(catalog, id) {
    return catalog.themes.find((theme) => theme.id === id);
}
export function asTypedRequest(request) {
    return request;
}
//# sourceMappingURL=validate.js.map